'use client';

import { URLParameters } from '@/app/common/urlParameters';
import {
  captureInitialModalStack,
  dropRestorableModals,
  getCurrentModals,
  settleOpenedModal,
  takeRestorableModal,
  useNextRestorableModal,
} from '@/app/common/modalStack';
import useCCMShown from '@/app/common/hooks/useCCMShown';
/* eslint-disable no-restricted-globals */
import { useEffect, useRef } from 'react';

export function useBrowserBackButton({
  condition,
  disabled = false,
  modalId,
  onOpen,
  onClose,
  onFinallyHistoryBack,
}: {
  condition: boolean | undefined;
  disabled?: boolean;
  modalId: string;
  onOpen?: () => void;
  onClose?: () => void;
  onFinallyHistoryBack?: () => void;
}) {
  const updatedCondition = useRef<boolean | undefined>(condition);
  const historyBackRef = useRef(false);
  // Whether the entry currently on top of the history stack is the one THIS modal pushed. Closing
  // may only call history.back() when it is: a modal reopened from a restored stack never pushed
  // one, and backing out of it would pop somebody else's entry. (MPH-965)
  const pushedHistoryEntryRef = useRef(false);
  // Whether this instance has actually opened the modal. Consumers seed their state differently
  // (undefined vs false), so a remount over a restored stack can run this effect with condition
  // false for a modal that is legitimately listed — that is not a close and must not touch the
  // url, or the id gets trimmed and then re-appended at the end, reordering the stack. (MPH-965)
  const hasOpenedRef = useRef(false);
  // MPH-1137: a modal opened on arrival is a Radix dialog, and a modal Radix dialog writes
  // pointer-events:none on <body>, handing it back only to its own overlay and paper. The cookie
  // banner is injected as a sibling of those, outside the dialog's tree, so it inherits none: every
  // control on it stops responding — "Accept" included, and the bottom variant has no close button
  // of its own — and the banner can no longer be dismissed at all. It covers the page until it is
  // answered anyway, so the restore below waits for it rather than racing it.
  // No isLocal opt-out on purpose: the banner does come up on a dev machine too, just later than it
  // does in production, so the bug — and the fix — reproduce there as they do anywhere else.
  // Only the modal next in line to be reopened has anything to wait for, so only that instance polls
  // (the next one in line only gets its turn once this one is open, long after the banner). Every
  // other instance — the OpenXModal shells that mount closed, the room, gallery and transport
  // providers — reports settled at once and starts no interval. Where no banner is coming at all
  // (app webview, blocked script) the wait gives up on its own; see useCCMShown.
  useEffect(() => {
    if (!disabled) captureInitialModalStack();
  }, [disabled]);
  const nextRestorableModal = useNextRestorableModal();
  const mayRestore = !disabled && nextRestorableModal === modalId;
  const isCCMShown = useCCMShown(undefined, { settleWithoutBanner: true, enabled: mayRestore });

  function setCurrentState() {
    const currentModalsArray = getCurrentModals();
    const isIncluded = currentModalsArray.includes(modalId);

    if (isIncluded && !updatedCondition.current) {
      onOpen?.();
    } else if (!isIncluded && updatedCondition.current) {
      // the navigation already took the entry this modal had pushed, if any
      pushedHistoryEntryRef.current = false;
      hasOpenedRef.current = false;
      onClose?.();
    }
  }

  function handlePopState(event: { state: { url: string } }) {
    if (historyBackRef.current) {
      historyBackRef.current = false;
      onFinallyHistoryBack?.();
    }
    if (event.state) {
      setCurrentState();
    }
  }

  useEffect(() => {
    if (!disabled) {
      window.addEventListener('popstate', handlePopState, false);
    }
    return () => {
      if (!disabled) {
        window.removeEventListener('popstate', handlePopState);
      }
    };
  }, [modalId]);

  useEffect(() => {
    // Nothing to restore while the cookie banner is still up: opening now would make it
    // undismissable (see isCCMShown above). PageWrapperClient hands the stack over before it strips
    // ?modal=, so waiting here costs nothing — the arrival stack outlives the url. mayRestore starts
    // false, so this does not fire before the peek has said whether there is anything to restore
    // at all. (MPH-1137)
    if (!mayRestore || !isCCMShown) return;

    // MPH-985: restore from the stack the page arrived on, not from the url as it stands when
    // this instance happens to mount. Providers do not all mount in the same commit, and by then
    // the url has been rewritten (PageWrapperClient strips ?modal= on mount, the outer modal
    // re-lists itself). A late provider read a stack that no longer described the page and opened
    // itself on top of what the user had just asked for. MPH-969: every modal of that stack is
    // restorable once, in order — an inner modal waits until the modal below it is open again —
    // and only until the user opens or closes a modal themselves.
    if (condition !== true && !updatedCondition.current && takeRestorableModal(modalId)) {
      onOpen?.();
    }
  }, [modalId, mayRestore, isCCMShown]);

  useEffect(() => {
    async function handleConditionChange() {
      const currentModalsArray = getCurrentModals();
      const [lastModal] = currentModalsArray.slice(-1);
      const isOnTop = lastModal === modalId;

      // MPH-965: only push when this modal is not in the stack at all. Guarding on isOnTop alone
      // allowed the same id to be appended twice — e.g. going back to a history entry whose modal
      // param still lists both modals: the provider remounts closed, setCurrentState() reopens it
      // because includes() finds it further down, and this effect appended a second copy. From then
      // on the modal could not be closed: history.back() popped one copy, includes() still matched
      // the other, the modal reopened and re-pushed, so the url never changed.
      if (condition === true) {
        hasOpenedRef.current = true;
        if (!currentModalsArray.includes(modalId)) {
          currentModalsArray.push(modalId);
          const url = new URL(window.location.href);
          url.searchParams.set(URLParameters.MODAL, currentModalsArray.join(','));
          history.pushState({ modal: modalId }, '', url);
          pushedHistoryEntryRef.current = true;
        }
        // Open and listed, pushed or already there (a back navigation lands on an entry that lists
        // it). If the user opened this modal, nothing that mounts from here on may reopen itself from
        // the stack the page arrived on (MPH-985); if it was next in line, the modal stacked on top of
        // it gets its turn now, and only now, so it cannot be listed before it. (MPH-969)
        settleOpenedModal(modalId);
      } else if (condition === false && hasOpenedRef.current && currentModalsArray.includes(modalId)) {
        hasOpenedRef.current = false;
        // Whatever of the arrival stack was still to come was opened from this modal. (MPH-969)
        dropRestorableModals();
        if (pushedHistoryEntryRef.current && isOnTop) {
          pushedHistoryEntryRef.current = false;
          // This timeout is to wait for the modal to finish the animation and fully close removing the modal from the DOM and restoring the scroll block of the body
          setTimeout(() => {
            historyBackRef.current = true;
            history.back();
          }, 300);
        } else {
          // The modal is listed in the stack but has no history entry of its own: it was reopened
          // from a stack restored by a back navigation, so the entry on top belongs to whatever ran
          // before it. Going back would leave that other modal open instead of closing this one, so
          // just drop this id from the current entry and leave the history alone. (MPH-965)
          const remainingModals = currentModalsArray.filter((currentModal) => currentModal !== modalId);
          const url = new URL(window.location.href);
          if (remainingModals.length) {
            url.searchParams.set(URLParameters.MODAL, remainingModals.join(','));
          } else {
            url.searchParams.delete(URLParameters.MODAL);
          }
          history.replaceState(history.state, '', url);
        }
      }
    }

    if (!disabled) {
      updatedCondition.current = condition;
      handleConditionChange();
    }
  }, [condition]);
}
